"use client";

import { useEffect } from "react";

// Last-resort boundary for errors thrown in the ROOT layout itself (which app/error.tsx can't
// catch, because it renders *inside* that layout). It replaces the whole document, so it must
// render its own <html>/<body> and can't rely on the app's providers. Kept dependency-free and
// styled inline so it renders even if the app shell or stylesheet failed to load. The customer
// only ever sees a friendly message — never the error text, a stack trace or a code.
export default function GlobalError({
  error,
  reset
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("Fatal portal error:", error);
  }, [error]);

  return (
    <html lang="en">
      <body style={{ margin: 0, fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif", background: "#f8fafc", color: "#0f172a" }}>
        <main style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <div style={{ width: "100%", maxWidth: "420px", background: "#ffffff", border: "1px solid rgba(226,232,240,0.7)", borderRadius: "16px", padding: "32px", textAlign: "center", boxShadow: "0 1px 3px rgba(16,24,40,0.06)" }}>
            <div style={{ margin: "0 auto", height: "48px", width: "48px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "9999px", background: "#fef2f2", color: "#dc2626" }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} style={{ height: "24px", width: "24px" }}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <h1 style={{ marginTop: "16px", fontSize: "18px", fontWeight: 700, letterSpacing: "-0.01em" }}>Something went wrong</h1>
            <p style={{ marginTop: "6px", fontSize: "14px", color: "#475569", lineHeight: 1.5 }}>
              The app hit an unexpected problem. Please reload the page, and contact support if it keeps happening.
            </p>
            {error.digest && (
              <p style={{ marginTop: "12px", fontFamily: "ui-monospace, monospace", fontSize: "12px", color: "#94a3b8" }}>
                Reference: {error.digest}
              </p>
            )}
            <div style={{ marginTop: "24px", display: "flex", justifyContent: "center", gap: "12px" }}>
              <button
                onClick={reset}
                style={{ borderRadius: "8px", background: "#0f4c81", color: "#ffffff", border: "none", padding: "8px 16px", fontSize: "14px", fontWeight: 500, cursor: "pointer" }}
              >
                Try again
              </button>
              <a
                href="/"
                style={{ borderRadius: "8px", background: "#ffffff", color: "#334155", border: "1px solid #cbd5e1", padding: "8px 16px", fontSize: "14px", fontWeight: 500, textDecoration: "none" }}
              >
                Go home
              </a>
            </div>
          </div>
        </main>
      </body>
    </html>
  );
}
